import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    FormBuilder,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import { ISettingsMailSmtp } from '@models';
import UiValidators, {
    UI_EMAIL_DOMAIN_MAX_LENGTH,
    UI_EMAIL_HOST_MAX_LENGTH,
    UI_EMAIL_MAX_LENGTH,
    UI_NAME_LONG_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
    UI_SMTP_PASSWORD_MAX_LENGTH,
} from '@validators';

import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'ui-mail-settings',
    templateUrl: './ui-mail-settings.component.html',
    styleUrls: ['../ui-setting-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiMailSettingsComponent extends UiSettingForm implements OnInit, OnDestroy {
    @Output()
    public smtpSettingChanged = new EventEmitter<boolean>();

    private _smtpSettingControls: (keyof ISettingsMailSmtp)[] = [
        'smtpUseDefaultCredentials',
        'smtpEnableSsl',
        'smtpHost',
        'smtpPort',
        'smtpDomain',
        'smtpUsername',
        'smtpPassword',
        'defaultFromAddress',
        'defaultFromDisplayName',
    ];

    constructor(
        private _fb: FormBuilder,
        cd: ChangeDetectorRef,
        appState: AppStateFacade,
    ) {
        super(
            cd,
            appState,
        );
    }

    ngOnInit() {
        this.formGroup = this._fb.group({
            'alertsEmailEnabled': [
                this.settings.mail.alertsEmailEnabled,
            ],
            'smtpHost': [
                this.settings.mail.smtpHost,
                Validators.compose([
                    UiValidators.whitespace,
                    Validators.maxLength(UI_EMAIL_HOST_MAX_LENGTH),
                ]),
            ],
            'smtpPort': [
                this.settings.mail.smtpPort,
                UiValidators.range(1, 65535, true),
            ],
            'smtpDomain': [
                this.settings.mail.smtpDomain,
                Validators.compose([
                    UiValidators.whitespace,
                    Validators.maxLength(UI_EMAIL_DOMAIN_MAX_LENGTH),
                ]),
            ],
            'smtpUsername': [
                this.settings.mail.smtpUsername,
                Validators.compose([
                    UiValidators.whitespace,
                    Validators.maxLength(UI_NAME_LONG_MAX_LENGTH),
                ]),
            ],
            'smtpPassword': [
                this.settings.mail.smtpPassword,
                UiValidators.maxLength(UI_SMTP_PASSWORD_MAX_LENGTH),
            ],
            'smtpUseDefaultCredentials': [
                this.settings.mail.smtpUseDefaultCredentials,
            ],
            'smtpEnableSsl': [
                this.settings.mail.smtpEnableSsl,
            ],
            'defaultFromAddress': [
                this.settings.mail.defaultFromAddress,
                Validators.compose([
                    UiValidators.maxLength(UI_EMAIL_MAX_LENGTH),
                    UiValidators.email(true),
                ]),
            ],
            'defaultFromDisplayName': [
                this.settings.mail.defaultFromDisplayName,
                Validators.compose([
                    UiValidators.whitespace,
                    Validators.maxLength(UI_NAME_MAX_LENGTH),
                ]),
            ],
        });

        const disabledControls = ['alertsEmailEnabled', 'smtpUseDefaultCredentials', 'smtpEnableSsl'];
        this.isReadOnly$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    disabledControls.forEach(controlName => this.formGroup.get(controlName)!.disable());
                } else {
                    disabledControls.forEach(controlName => this.formGroup.get(controlName)!.enable());
                }
            });

        this.formGroup.valueChanges
            .pipe(takeUntil(this._destroyed$))
            .subscribe(value => Object.assign(this.settings.mail, value));
    }

    public toggleSmtpSettingChangeStatus() {
        this.smtpSettingChanged.emit(this._smtpSettingControls.some(controlName => this.formGroup.get(controlName)!.dirty));
    }
}
